import { Box } from '@rocket/ui';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useState } from 'react';

import { useColorMode } from '../../styles/colorMode';
import { AG_GRID_STYLE_OVERRIDES, agGridTheme, COLUMN_TYPES } from '../AGTable/AGTable.utils';
import { ensureAgGridSetup } from '../AGTable/ag-grid-setup';

// Lazily register AG Grid enterprise modules on first grid render
ensureAgGridSetup();

// biome-ignore lint/suspicious/noExplicitAny: ag-grid row data, colDefs and event params are dynamic untyped shapes
type AnyData = any;

interface AGServerTableProps {
  sx?: AnyData;
  rowData?: AnyData;
  colDefs?: AnyData;
  rowHeight?: number;
  rowBuffer?: number;
  cacheSize?: number;
  cacheBlocks?: number;
  isSelectable?: boolean;
  isMoveable?: boolean;
  isEditable?: boolean;
  isFilterFloating?: boolean;
  isSidebarVisible?: boolean;
  isGroupingVisible?: boolean;
  isAutoResize?: boolean;
  pagination?: Array<number>;
  defaultPagination?: number;
  contextMenuItems?: AnyData;
  sideBarItems?: AnyData;
  serverDataSource?: AnyData;
  onReady?: AnyData;
  onRowClick?: (rowIndex: number | null) => void;
  onUpdate?: (api: AnyData) => void;
  onSelect?: (rows: Array<AnyData>) => void;
  rowSelection?: string | AnyData;
  // ag-grid passthrough props (serverSideDatasource, getRowId, getContextMenuItems, etc.)
  [key: string]: AnyData;
}

const AGServerTable = ({
  sx,
  rowData,
  colDefs,
  rowHeight = 42,
  rowBuffer = 60,
  cacheSize = 500,
  cacheBlocks = 10,
  isSelectable = false,
  isMoveable = true,
  isEditable = false,
  isFilterFloating = false,
  isSidebarVisible = false,
  isGroupingVisible = false,
  isAutoResize = false,
  pagination,
  defaultPagination = 50,
  contextMenuItems = null,
  sideBarItems = null,
  onReady,
  onRowClick,
  onUpdate,
  onSelect,
  rowSelection = null,
  ...rest
}: AGServerTableProps) => {
  const { colorMode } = useColorMode();
  // AG Grid needs the columns to be stored in state
  const [AGcolDefs, setAGcolDefs] = useState(colDefs);
  useEffect(() => {
    // Update stored column definitions
    if (colDefs) {
      setAGcolDefs(colDefs);
    }
  }, [colDefs]);

  // Default column settings
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy dep array preserved verbatim
  const defaultColDef = useMemo(() => {
    const contextItems = contextMenuItems || [];
    const colDef: AnyData = {
      type: 'string',
      minWidth: 72,
      suppressMovable: !isMoveable,
      floatingFilter: isFilterFloating,
      filterParams: {
        maxNumConditions: 10,
        buttons: ['clear'],
      },
      contextMenuItems: [...contextItems, 'copy', 'export'],
    };

    // Bypass the config's context menu when the function getter is provided
    if (rest.getContextMenuItems) {
      delete colDef.contextMenuItems;
    }

    return colDef;
  }, [isMoveable, isFilterFloating, contextMenuItems]);

  // Side bar settings
  const sideBarDef = useMemo(() => {
    const sideItems = sideBarItems || [];
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressExpandAll: false,
            suppressFilterSearch: false,
          },
        },
        ...sideItems,
      ],
      defaultToolPanel: 'columns',
    };
  }, [sideBarItems]);

  // Row selection settings
  const rowSelectionDefault = useMemo(() => {
    return isSelectable
      ? {
          mode: 'multiRow',
          selectAll: 'filtered',
          enableClickSelection: false,
        }
      : { checkboxes: false, headerCheckbox: false, enableClickSelection: false };
  }, [isSelectable]);

  return (
    <Box
      css={{
        ...AG_GRID_STYLE_OVERRIDES,
        ...sx,
      }}
    >
      <AgGridReact
        theme={agGridTheme(colorMode)}
        rowModelType="serverSide"
        editType="fullRow"
        columnTypes={COLUMN_TYPES}
        columnDefs={AGcolDefs}
        suppressClickEdit={!isEditable}
        pagination={Boolean(pagination?.length)}
        paginationPageSize={defaultPagination}
        paginationPageSizeSelector={pagination}
        sideBar={isSidebarVisible ? sideBarDef : {}}
        rowGroupPanelShow={(isGroupingVisible ? 'always' : '') as AnyData}
        autoSizeStrategy={isAutoResize ? { type: 'fitCellContents' } : undefined}
        cacheBlockSize={cacheSize}
        maxBlocksInCache={cacheBlocks}
        onGridReady={onReady}
        onRowClicked={(e) => onRowClick?.(e.rowIndex)}
        onModelUpdated={(e) => onUpdate?.(e.api)}
        onSelectionChanged={(e) => onSelect?.(e.api.getSelectedRows())}
        rowSelection={{ ...rowSelectionDefault, ...(rowSelection || {}) }}
        {...{ defaultColDef, rowHeight, rowBuffer, rowData }}
        {...rest}
      />
    </Box>
  );
};

export default AGServerTable;
